import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/airbnb.css";
import Moment from "moment";
import { IconCalendar, IconX } from "@tabler/icons";
import { count, fyOf, fyRange } from "./format";

// Form controls. Text-like inputs keep native semantics (onChange gets the
// event; onValueChange gets the value). Checkbox / Switch call
// onChange(checked, event); SegmentedControl, SearchSelect and
// DateRangeInput call onChange(value).

let idSeq = 0;
const useAutoId = (prefix) => {
  const [id] = useState(() => `${prefix}-${++idSeq}`);
  return id;
};

/* ------------------------------------------------------------------ */
/* Field                                                               */
/* ------------------------------------------------------------------ */

// Label + control + hint/error. A single child control gets the id,
// aria-describedby and aria-invalid wired automatically.
export function Field({ label, hint, error, required, htmlFor, children, className = "", style }) {
  const autoId = useAutoId("pa-field");
  const single = React.isValidElement(children) ? children : null;
  const id = htmlFor || (single && single.props.id) || autoId;
  const descId = hint || error ? `${id}-desc` : undefined;
  const extra = single
    ? {
        id,
        "aria-describedby": [single.props["aria-describedby"], descId].filter(Boolean).join(" ") || undefined,
        "aria-invalid": error ? true : single.props["aria-invalid"],
      }
    : null;
  // Only kit controls understand `invalid`; DOM elements would warn about it.
  if (single && KIT_CONTROLS.has(single.type)) extra.invalid = error ? true : single.props.invalid;
  const control = single ? React.cloneElement(single, extra) : children;
  return (
    <div className={`pa-field ${error ? "has-error" : ""} ${className}`} style={style}>
      {label && (
        <label htmlFor={id}>
          {label}
          {required && (
            <span className="pa-required" aria-hidden="true">
              {" "}
              *
            </span>
          )}
        </label>
      )}
      {control}
      {error ? (
        <div id={descId} className="pa-field-error">
          {error}
        </div>
      ) : hint ? (
        <div id={descId} className="pa-field-hint">
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Text, number, select                                                */
/* ------------------------------------------------------------------ */

// prefix / suffix: short adornments (e.g. "₹", an icon, "%").
const Adorned = ({ prefix, suffix, invalid, disabled, children }) =>
  prefix || suffix ? (
    <div className={`pa-input-group ${invalid ? "is-invalid" : ""} ${disabled ? "is-disabled" : ""}`}>
      {prefix && <span className="pa-input-affix">{prefix}</span>}
      {children}
      {suffix && <span className="pa-input-affix">{suffix}</span>}
    </div>
  ) : (
    children
  );

export const TextInput = React.forwardRef(function TextInput(
  { prefix, suffix, invalid, multiline, onChange, onValueChange, className = "", ...props },
  ref
) {
  const handle = (e) => {
    if (onChange) onChange(e);
    if (onValueChange) onValueChange(e.target.value);
  };
  const cls = `pa-input ${invalid ? "is-invalid" : ""} ${className}`;
  const el = multiline ? (
    <textarea ref={ref} className={cls} onChange={handle} {...props} />
  ) : (
    <input ref={ref} type="text" className={cls} onChange={handle} {...props} />
  );
  return (
    <Adorned prefix={prefix} suffix={suffix} invalid={invalid} disabled={props.disabled}>
      {el}
    </Adorned>
  );
});

// Number field that never changes on mouse wheel. The value stays a string so
// partial input ("12.") survives; parse it where you use it.
export const NumberInput = React.forwardRef(function NumberInput(
  { prefix, suffix, invalid, onChange, onValueChange, className = "", step = "any", ...props },
  ref
) {
  const handle = (e) => {
    if (onChange) onChange(e);
    if (onValueChange) onValueChange(e.target.value);
  };
  return (
    <Adorned prefix={prefix} suffix={suffix} invalid={invalid} disabled={props.disabled}>
      <input
        ref={ref}
        type="number"
        inputMode="decimal"
        step={step}
        className={`pa-input pa-num ${invalid ? "is-invalid" : ""} ${className}`}
        onChange={handle}
        onWheel={(e) => e.currentTarget.blur()}
        {...props}
      />
    </Adorned>
  );
});

// options: [{ value, label, disabled? }] (or pass <option> children).
export const SelectInput = React.forwardRef(function SelectInput(
  { options, placeholder, invalid, onChange, onValueChange, className = "", children, ...props },
  ref
) {
  const handle = (e) => {
    if (onChange) onChange(e);
    if (onValueChange) onValueChange(e.target.value);
  };
  return (
    <select ref={ref} className={`pa-input pa-select ${invalid ? "is-invalid" : ""} ${className}`} onChange={handle} {...props}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options
        ? options.map((o) => (
            <option key={String(o.value)} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))
        : children}
    </select>
  );
});

/* ------------------------------------------------------------------ */
/* SearchSelect (react-select themed)                                  */
/* ------------------------------------------------------------------ */

// The menu is portalled to <body> (outside the .pa token scope), so these
// mirror the ui.css tokens as literal values.
const RS = {
  ink: "#151a24",
  muted: "#646b7a",
  line: "#e4e7ee",
  lineStrong: "#d0d5df",
  hover: "#b9c0cd",
  soft: "#f7f8fb",
  brand: "#42378f",
  tint: "#f0eef9",
  red: "#c2262e",
  purple: "#5b3fb0",
  purpleBg: "#efeafb",
  focus: "0 0 0 3px rgba(66, 55, 143, 0.35)",
};

const selectStyles = (invalid) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    borderRadius: 8,
    borderColor: invalid ? RS.red : state.isFocused ? RS.brand : RS.lineStrong,
    boxShadow: state.isFocused ? RS.focus : "none",
    backgroundColor: state.isDisabled ? RS.soft : "#fff",
    fontSize: 13,
    "&:hover": { borderColor: state.isFocused ? RS.brand : RS.hover },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  placeholder: (base) => ({ ...base, color: RS.muted }),
  singleValue: (base) => ({ ...base, color: RS.ink }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base) => ({ ...base, padding: 6 }),
  clearIndicator: (base) => ({ ...base, padding: 6 }),
  menu: (base) => ({ ...base, borderRadius: 10, overflow: "hidden", boxShadow: "0 12px 32px rgba(16, 24, 40, 0.14)", border: `1px solid ${RS.line}` }),
  menuPortal: (base) => ({ ...base, zIndex: 2100 }),
  noOptionsMessage: (base) => ({ ...base, fontSize: 13, color: RS.muted }),
  loadingMessage: (base) => ({ ...base, fontSize: 13, color: RS.muted }),
  option: (base, state) => ({
    ...base,
    fontSize: 13,
    cursor: "pointer",
    color: state.isSelected ? "#fff" : RS.ink,
    backgroundColor: state.isSelected ? RS.brand : state.isFocused ? RS.tint : "#fff",
  }),
  multiValue: (base) => ({ ...base, backgroundColor: RS.purpleBg, borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: RS.purple, fontWeight: 600, fontSize: 12 }),
  multiValueRemove: (base) => ({ ...base, color: RS.purple, ":hover": { backgroundColor: "rgba(91,63,176,.18)", color: RS.purple } }),
});

const findOption = (options, v) => {
  if (v && typeof v === "object") return v;
  return (options || []).find((o) => String(o.value) === String(v)) || null;
};

// Searchable select. value may be a raw value (or array for isMulti) that is
// looked up in options, or option objects. onChange(value, option) gets the
// raw value(s). Pass loadOptions(input) => Promise<options> for async search.
export function SearchSelect({ id, options = [], value, onChange, isMulti, loadOptions, invalid, placeholder = "Select…", ...rest }) {
  const menuOpen = useRef(false);
  const { onMenuOpen, onMenuClose, onKeyDown } = rest;
  const selected = isMulti
    ? (Array.isArray(value) ? value : []).map((v) => findOption(options, v) || (typeof v === "object" ? v : { value: v, label: String(v) }))
    : value === "" || value === null || value === undefined
    ? null
    : findOption(options, value) || { value, label: String(value) };
  const common = {
    inputId: id,
    value: selected,
    isMulti,
    placeholder,
    classNamePrefix: "pa-rs",
    styles: selectStyles(invalid),
    menuPortalTarget: typeof document !== "undefined" ? document.body : undefined,
    menuPlacement: "auto",
    "aria-invalid": invalid || undefined,
    onChange: (opt) => {
      if (!onChange) return;
      if (isMulti) onChange((opt || []).map((o) => o.value), opt || []);
      else onChange(opt ? opt.value : "", opt);
    },
    ...rest,
    onMenuOpen: () => {
      menuOpen.current = true;
      if (onMenuOpen) onMenuOpen();
    },
    onMenuClose: () => {
      menuOpen.current = false;
      if (onMenuClose) onMenuClose();
    },
    // An Esc that closes the menu stays here, so an enclosing Drawer (which
    // listens on document) doesn't close too. react-select still handles it.
    onKeyDown: (e) => {
      if (e.key === "Escape" && (menuOpen.current || rest.menuIsOpen)) e.stopPropagation();
      if (onKeyDown) onKeyDown(e);
    },
  };
  return loadOptions ? (
    <AsyncSelect cacheOptions defaultOptions loadOptions={loadOptions} {...common} />
  ) : (
    <Select options={options} {...common} />
  );
}

/* ------------------------------------------------------------------ */
/* DateRangeInput                                                      */
/* ------------------------------------------------------------------ */

const ymd = (d) => Moment(d).format("YYYY-MM-DD");

export const DATE_PRESETS = [
  { label: "This month", range: () => ({ from: ymd(Moment().startOf("month")), to: ymd(Moment()) }) },
  {
    label: "Last month",
    range: () => ({ from: ymd(Moment().subtract(1, "month").startOf("month")), to: ymd(Moment().subtract(1, "month").endOf("month")) }),
  },
  { label: "Last 30 days", range: () => ({ from: ymd(Moment().subtract(29, "days")), to: ymd(Moment()) }) },
  { label: "This FY", range: () => ({ from: fyRange(fyOf()).from, to: ymd(Moment()) }) },
];

// value: { from, to } ('YYYY-MM-DD' or ''); onChange({ from, to }). The value
// is emitted once the range is complete; a single day picked and the calendar
// closed becomes from = to. presets: true (DATE_PRESETS) or your own list of
// { label, range: () => ({ from, to }) }.
export function DateRangeInput({
  id,
  value,
  onChange,
  placeholder = "Any date",
  presets,
  minDate,
  maxDate,
  invalid,
  disabled,
  clearable = true,
  "aria-describedby": describedBy,
}) {
  const from = (value && value.from) || "";
  const to = (value && value.to) || "";
  const dates = useMemo(() => [from, to].filter(Boolean).map((d) => Moment(d, "YYYY-MM-DD").toDate()), [from, to]);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastRef = useRef({ from, to });
  lastRef.current = { from, to };
  // Always points to the latest dates array so onOpen can read it without being
  // a dependency of the memoized options object.
  const datesRef = useRef(dates);
  datesRef.current = dates;
  const emit = (next) => {
    const cur = lastRef.current;
    if (next.from === cur.from && next.to === cur.to) return;
    if (onChangeRef.current) onChangeRef.current(next);
  };
  // Stable options: react-flatpickr re-applies any option whose identity changes.
  const options = useMemo(() => {
    // Flatpickr's minMaxDateSetter parses minDate/maxDate using the configured
    // dateFormat ("j M Y"). Passing a YYYY-MM-DD string therefore corrupts the
    // limit (e.g. "2026-10-15" gets mis-parsed as day=2026, month=January).
    // The fix: convert YYYY-MM-DD strings to Date objects, which parseDate
    // returns as-is without any format parsing. "today" is a Flatpickr keyword
    // handled natively so we leave it alone.
    const toDate = (v) => {
      if (!v || v instanceof Date || v === "today") return v;
      const d = Moment(v, "YYYY-MM-DD", true);
      return d.isValid() ? d.toDate() : v;
    };
    return {
      mode: "range",
      dateFormat: "j M Y",
      minDate: toDate(minDate),
      maxDate: toDate(maxDate),
      disableMobile: true,
      onReady: (_d, _s, fp) => fp.calendarContainer && fp.calendarContainer.classList.add("pa-flatpickr"),
      // react-flatpickr calls setDate(value, false) on mount, which sets the
      // selected dates but does NOT call jumpToDate (because triggerChange=false).
      // Flatpickr therefore keeps its default view month (January). Fix: jump to
      // the start date whenever the calendar opens.
      onOpen: (_d, _s, fp) => {
        const start = datesRef.current[0] || new Date();
        fp.jumpToDate(start, false);
      },
      // Emitting after the first click would feed [d, d] back into the picker
      // and end the range selection early, so wait for the second day.
      onChange: (picked) => {
        if (picked.length === 0) emit({ from: "", to: "" });
        else if (picked.length === 2) emit({ from: ymd(picked[0]), to: ymd(picked[1]) });
      },
      onClose: (picked, _s, fp) => {
        if (picked.length === 1) emit({ from: ymd(picked[0]), to: ymd(picked[0]) });
        if (fp && fp.input && fp.input.blur) fp.input.blur();
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDate, maxDate]);
  const list = presets === true ? DATE_PRESETS : Array.isArray(presets) ? presets : null;
  return (
    <div className="pa-daterange">
      <div className={`pa-input-group ${invalid ? "is-invalid" : ""} ${disabled ? "is-disabled" : ""}`}>
        <span className="pa-input-affix" aria-hidden="true">
          <IconCalendar size={16} stroke={1.8} />
        </span>
        <Flatpickr
          id={id}
          className="pa-input"
          value={dates}
          options={options}
          placeholder={placeholder}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
        />
        {clearable && (from || to) && !disabled && (
          <button type="button" className="pa-input-clear" aria-label="Clear dates" onClick={() => emit({ from: "", to: "" })}>
            <IconX size={14} stroke={2} />
          </button>
        )}
      </div>
      {list && (
        <div className="pa-presets" role="group" aria-label="Date presets">
          {list.map((p) => {
            const r = p.range();
            const on = r.from === from && r.to === to;
            return (
              <button key={p.label} type="button" className={`pa-preset ${on ? "is-active" : ""}`} aria-pressed={on} onClick={() => emit(r)} disabled={disabled}>
                {p.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Checkbox, Switch, SegmentedControl                                  */
/* ------------------------------------------------------------------ */

export function Checkbox({ id, checked, indeterminate, onChange, label, description, disabled, className = "", ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate) && !checked;
  }, [indeterminate, checked]);
  return (
    <label className={`pa-check ${disabled ? "is-disabled" : ""} ${className}`}>
      <input
        ref={ref}
        id={id}
        type="checkbox"
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked, e)}
        {...rest}
      />
      <span className="pa-check-box" aria-hidden="true" />
      {(label || description) && (
        <span className="pa-check-text">
          {label}
          {description && <span className="pa-check-desc">{description}</span>}
        </span>
      )}
    </label>
  );
}

export function Switch({ id, checked, onChange, label, description, disabled, className = "", ...rest }) {
  return (
    <label className={`pa-switch ${disabled ? "is-disabled" : ""} ${className}`}>
      <input
        id={id}
        type="checkbox"
        role="switch"
        aria-checked={Boolean(checked)}
        checked={Boolean(checked)}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked, e)}
        {...rest}
      />
      <span className="pa-switch-track" aria-hidden="true">
        <span className="pa-switch-thumb" />
      </span>
      {(label || description) && (
        <span className="pa-check-text">
          {label}
          {description && <span className="pa-check-desc">{description}</span>}
        </span>
      )}
    </label>
  );
}

// A radio group styled as a segmented button. options: [{ value, label, count?, disabled? }]
export function SegmentedControl({ options, value, onChange, ariaLabel = "Options", size, block, disabled }) {
  const refs = useRef([]);
  // The option in the tab order: the selected one, or the first enabled option
  // while value matches no enabled option.
  const selectedIndex = options.findIndex((o) => String(o.value) === String(value) && !o.disabled);
  const tabStop = selectedIndex >= 0 ? selectedIndex : options.findIndex((o) => !o.disabled);
  const move = (i, dir) => {
    const n = options.length;
    for (let k = 1; k <= n; k++) {
      const j = (i + dir * k + n) % n;
      if (!options[j].disabled) {
        onChange(options[j].value);
        if (refs.current[j]) refs.current[j].focus();
        return;
      }
    }
  };
  return (
    <div className={`pa-seg ${size ? "pa-seg-" + size : ""} ${block ? "pa-seg-block" : ""}`} role="radiogroup" aria-label={ariaLabel}>
      {options.map((o, i) => {
        const on = String(o.value) === String(value);
        return (
          <button
            key={String(o.value)}
            ref={(el) => (refs.current[i] = el)}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={i === tabStop ? 0 : -1}
            className={`pa-seg-item ${on ? "is-active" : ""}`}
            disabled={disabled || o.disabled}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                move(i, 1);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                move(i, -1);
              }
            }}
          >
            {o.label}
            {o.count !== undefined && o.count !== null && <span className="pa-tab-count">{count(o.count)}</span>}
          </button>
        );
      })}
    </div>
  );
}

// Controls that accept the `invalid` prop Field injects.
const KIT_CONTROLS = new Set([TextInput, NumberInput, SelectInput, SearchSelect, DateRangeInput]);
